
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { BuyingCalendarModel } from '@/models/buying_calendar';
import {
  IBuyingListItem,
  IBuyingList,
  IProduct,
  ISupplier,
  IViewMode,
  ISupplierGroup,
  DateRange
} from '@/models/buying_list';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
// import { BuyingListModel } from '@/models/buying_list'; // Unused import
import { PurchaseOrderModel, IPurchaseOrderItem } from '@/models/purchase_order';
import { TaxTypeList } from '@/collections/tax_types';
import { TaxTypeModel } from '@/models/tax_type';
import { EventBus } from "@/main";
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import DataSyncStatusBanner from '@/components/DataSyncStatusBanner.vue';
import LocationFilter from '@/components/LocationFilter.vue';
import ReportFilter from '@/components/ReportFilter.vue';
import { StoreModel } from '@/models/store';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import { ProductSegmentModel } from '@/models/product_segment';
import ReorderUserParams from '@/components/ReorderUserParams.vue';
import { MeasureList } from '@/collections/measures';
// import { MeasureModel } from '@/models/measure'; // Unused - measures come as prop
import { ParamList } from '@/collections/params';
import { ParamModel } from '@/models/param';
import { SupplierList } from '@/collections/suppliers';
import { DimensionModel } from '@/models/dimension';
import { AnalyticsTypeModel } from '@/models/analytics_type';
import { BuyingCalendarSettingsService, IBuyingCalendarSettings } from '@/services/buyingCalendarSettings';


interface FilterData {
  dimension: DimensionModel;
  analyticsType: AnalyticsTypeModel;
  dates: string[];
  filterColumns: FilterColumnList;
  reportParams: ParamList;
  productSegment: ProductSegmentModel;
}

@Component({
  name: 'BuyingListSuggestion',
  components: {
    DataSyncStatusBanner,
    LocationFilter,
    ReportFilter,
    ReorderUserParams
  }
})
export default class BuyingListSuggestion extends Vue {
  private loading = false;
  private loadingLocations = false;
  private paramsLoaded = false;
  private locations: StoreLocationModel[] = [];
  private storeLocationList = new StoreLocationList();
  private activeLocationFilters: FilterColumnList | null = null;
  @Prop({ default: () => new StoreModel() })
  private store!: StoreModel;
  @Prop({ default: () => new MeasureList() })
  public measures!: MeasureList;
  
  // NEW: Add measure property for ReportFilter component
  public measure: any = { filterDimensions: [] };
  // Data properties
  private viewMode: IViewMode = 'week';

  private suggestions: any = [];
  private selectedDimension = 'by_variant';
  private currentDimension = 'by_variant';
  private savedLocationIds: number[] = [];

  // Add this property to track loading state
  private supplierPoCreationState: { [key: string]: { loading: boolean; poId?: number; poCreated?: boolean } } = {};

  // Modify expandedGroups to use string keys for composite keys
  private expandedGroups: { [key: string]: boolean } = {};

  // Initialize with empty array instead of calling getDateRange()
  private _selectedDates: string[] = [];

  // Add new property for calendar loading state
  private calendarLoading = false;

  private displayMode: 'list' | 'calendar' = 'calendar';

  // Add new property for warning visibility

  // NEW: Parameter management properties
  private reportParams: ParamList = new ParamList();
  private historicalDateRange: string[] = [];

  // NEW: Filter properties for ReportFilter component
  private filterColumns: FilterColumnList = new FilterColumnList();
  private initFilterColumns: FilterColumnList = new FilterColumnList();
  private productSegment: ProductSegmentModel = new ProductSegmentModel();
  private initProductSegment: ProductSegmentModel = new ProductSegmentModel();
  private availableParams: ParamList = new ParamList();
  private filterData: FilterData = {
    dimension: new DimensionModel(),
    analyticsType: new AnalyticsTypeModel(),
    dates: [],
    filterColumns: new FilterColumnList(),
    reportParams: new ParamList(),
    productSegment: new ProductSegmentModel(),
  };

  // Report columns for filter conditions
  private reportColumns: any[] = [
    {
      name: "Product",
      code: "Item[Item Description]",
      dataType: "text",
      filterable: true,
      luisMapping: "by_product",
      description: "Filter by product name"
    },
    {
      name: "SKU",
      code: "product_sku", 
      dataType: "text",
      filterable: true,
      luisMapping: "by_product_sku",
      description: "Filter by SKU/product code"
    },
    {
      name: "Vendor",
      code: "Vendor Name",
      dataType: "text",
      filterable: true,
      luisMapping: "by_vendor", 
      description: "Filter by vendor/supplier name"
    },
    {
      name: "Product Type",
      code: "product_type",
      dataType: "text", 
      filterable: true,
      luisMapping: "by_product_type",
      description: "Filter by product category/type"
    },
    {
      name: "Product Tags",
      code: "product_tags",
      dataType: "text",
      filterable: true,
      luisMapping: "by_product_tag",
      description: "Filter by product tags"
    },
    {
      name: "Collections",
      code: "product_collections", 
      dataType: "text",
      filterable: true,
      luisMapping: "product_collections",
      description: "Filter by product collections"
    },
    {
      name: "Variant",
      code: "variant",
      dataType: "text",
      filterable: true,
      luisMapping: "variant",
      description: "Filter by product variant"
    },
    {
      name: "Barcode",
      code: "product_barcode",
      dataType: "text", 
      filterable: true,
      luisMapping: "product_barcode",
      description: "Filter by product barcode"
    }
  ];

  // Supplier filter properties
  private supplierFilter = 'all';
  private suppliers: SupplierList = new SupplierList();
  private taxTypeList: TaxTypeList = new TaxTypeList();
  private supplierOptions = [
    { text: 'All Suppliers', value: 'all' }
  ];

  // New properties for delivery location selection dialog
  private deliveryLocationDialogVisible: boolean = false;
  private selectedDeliveryLocationIdForPO: string | null = null;
  private currentSupplierForPoCreation: any = null;

  // Properties for supplier details dialog (ensure they are correctly defined)
  private showSupplierDialog = false;
  private selectedSupplierDetails: any = null;
  
  // Timeout for search debouncing
  private searchTimeout: any = null;

  // Timeout for settings auto-save debouncing
  private saveSettingsTimeout: any = null;

  // Guard flag to suppress watchers during initial settings hydration
  private settingsLoading = true;
  
  // Product limit configuration
  private productLimitPerSupplier: number = 100; // Default to 100
  
  private productLimitOptions = [
    { text: '100', value: 100 },
    { text: '200', value: 200 },
    { text: '500', value: 500 },
    { text: '1,000', value: 1000 }
  ];

  get isSkuMode(): boolean {
    return this.currentDimension === 'by_product_sku';
  }

  // New computed property to derive location IDs for API call
  get locationIdsForApi(): number[] {
    if (this.activeLocationFilters && this.activeLocationFilters.items.length > 0) {
      const locationFilter = this.activeLocationFilters.items.find(f => f.code === 'location_id');
      // Ensure value is an array and has items before casting and returning
      if (locationFilter && locationFilter.value && Array.isArray(locationFilter.value) && locationFilter.value.length > 0) {
        // Convert to numbers and filter out any NaNs that might result from conversion
        return locationFilter.value.map(id => Number(id)).filter(id => !isNaN(id)); 
      }
    }
    return []; // Return empty array for "all locations" or if no specific filter
  }

  // Computed properties
  get selectedDateRange(): DateRange {
    return {
      from: new Date(this.selectedDates[0]),
      to: new Date(this.selectedDates[1])
    };
  }

  public async created() {
    this.loading = true;
    try {
      // Initialize dates first — computed properties like calendarDays depend on it
      this._selectedDates = this.getDateRange();

      // Load saved buying calendar settings from backend
      await this.loadSavedSettings();
      this.loadMeasures(); // NEW: Validate measures for ReorderUserParams
      await this.fetchLocations();
      await this.fetchSuppliers(); // NEW: Load suppliers for the dropdown
      // NOTE: Don't call fetchBuyingCalendar here - wait for params to load first
      // fetchBuyingCalendar will be called from onReorderParamsLoaded
      // Listen for clear filter event from ReportFilter component
      EventBus.$on('clear-filter-column', () => {
        this.productSegment = new ProductSegmentModel();
        this.initProductSegment = new ProductSegmentModel();
        this.filterColumns = new FilterColumnList();
        this.filterData = {
          ...this.filterData,
          filterColumns: new FilterColumnList(),
          productSegment: new ProductSegmentModel()
        };
      });
    } catch (error) {
      this.handleError(error as Error, 'Component initialization failed');
      // Set loading to false if initialization fails
      this.loading = false;
    }
    // Note: Keep loading = true for successful initialization
    // Loading will be set to false when fetchBuyingCalendar completes in onReorderParamsLoaded
  }

  // Centralized error handling utility
  private handleError(error: Error, context: string, showToUser: boolean = true): void {
    console.error(`Error in ${context}:`, error);
    
    if (showToUser) {
      EventBus.$emit('show-snackbar', {
        message: `${context}: ${error.message || 'An unexpected error occurred'}`,
        color: 'error'
      });
    }
  }

  // Add method to clean up expanded groups periodically
  private cleanupExpandedGroups(): void {
    // Remove entries for suggestions that no longer exist
    const validKeys = new Set<string>();
    this.suggestions.forEach((suggestion: any, index: number) => {
      suggestion.suppliers.forEach((supplier: any) => {
        validKeys.add(this.getExpansionKey(index, supplier.id));
      });
    });
    
    Object.keys(this.expandedGroups).forEach(key => {
      if (!validKeys.has(key)) {
        this.$delete(this.expandedGroups, key);
      }
    });
  }

  // Handle product limit changes
  private async onProductLimitChange(): Promise<void> {
    this.debouncedSaveSettings();
    await this.fetchBuyingCalendar();
  }

  beforeDestroy() {
    // Clear reactive objects to prevent memory leaks
    this.expandedGroups = {};
    this.supplierPoCreationState = {};
    
    // Clear any pending timeouts
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    if (this.saveSettingsTimeout) {
      clearTimeout(this.saveSettingsTimeout);
      this.saveSettingsTimeout = null;
    }
    
    // Clear suggestions data
    this.suggestions = [];
    
    // Remove EventBus listeners
    EventBus.$off('clear-filter-column');
  }

  private async loadSavedSettings(): Promise<void> {
    this.settingsLoading = true;
    try {
      const settings = await BuyingCalendarSettingsService.get();
      this.viewMode = (settings.viewMode || 'week') as IViewMode;
      this.displayMode = (settings.displayMode || 'calendar') as 'list' | 'calendar';
      this.selectedDimension = settings.selectedDimension || 'by_variant';
      this.productLimitPerSupplier = settings.productLimitPerSupplier || 100;
      this.supplierFilter = settings.supplierFilter || 'all';

      // Pass saved location IDs to LocationFilter component via prop
      // The component will apply the filter and emit 'filter-applied' on created()
      if (settings.locationIds && settings.locationIds.length > 0) {
        this.savedLocationIds = settings.locationIds;
      }

      // Restore product filters
      if (settings.filterColumns && settings.filterColumns.length > 0) {
        this.filterColumns.mapData(settings.filterColumns);
        this.initFilterColumns = new FilterColumnList();
        this.initFilterColumns.mapData(settings.filterColumns);
        this.filterData = {
          ...this.filterData,
          filterColumns: this.filterColumns,
        };
      }

      // Restore product segment
      if (settings.productSegmentId) {
        this.productSegment = new ProductSegmentModel();
        this.productSegment.id = parseInt(settings.productSegmentId);
        this.initProductSegment = new ProductSegmentModel();
        this.initProductSegment.id = parseInt(settings.productSegmentId);
        this.filterData = {
          ...this.filterData,
          productSegment: this.productSegment,
        };
      }
    } catch (e) {
      // Silently fall back to defaults
      console.error('Failed to load buying calendar settings:', e);
    } finally {
      this.settingsLoading = false;
    }
  }

  private debouncedSaveSettings(): void {
    if (this.saveSettingsTimeout) clearTimeout(this.saveSettingsTimeout);
    this.saveSettingsTimeout = setTimeout(async () => {
      await BuyingCalendarSettingsService.save({
        locationIds: this.locationIdsForApi,
        supplierFilter: this.supplierFilter || 'all',
        filterColumns: this.filterColumns.items.map((f: any) => ({
          name: f.name,
          code: f.code,
          value: f.value,
          operator: f.operator || 'eq',
          dataType: f.dataType || 'text',
        })),
        productSegmentId: this.productSegment?.id?.toString() || null,
        viewMode: this.viewMode,
        displayMode: this.displayMode,
        selectedDimension: this.selectedDimension,
        productLimitPerSupplier: this.productLimitPerSupplier,
      });
    }, 2000);
  }

  public async fetchBuyingCalendar(): Promise<void> {
    this.loading = true;
    try {
      const buyingCalendarModel = new BuyingCalendarModel();
      
      // NEW: Combine reportParams with filterColumns for API call
      const combinedParams = new ParamList();
      
      // Add existing report parameters
      for (const param of this.reportParams.items) {
        combinedParams.add(param);
      }
      
      // Create combined product filters parameter (excluding product_segment_id)
      const productFilters: any[] = [];
      
      // Add filter columns
      for (const filter of this.filterColumns.items) {
        productFilters.push({
          name: filter.name,
          code: filter.code,
          value: filter.value,
          operator: filter.operator || 'eq',
          dataType: filter.dataType || 'text'
        });
      }
      
      // Add supplier filter
      if (this.supplierFilter && this.supplierFilter !== 'all') {
        productFilters.push({
          name: 'Supplier',
          code: 'supplier_id',
          value: [this.supplierFilter],
          operator: 'eq',
          dataType: 'number'
        });
      }
      
      // Add combined product filters as single parameter if any filters exist
      if (productFilters.length > 0) {
        const combinedFilterParam = new ParamModel({
          name: 'Product Filters',
          code: 'product_filters',
          value: [JSON.stringify(productFilters)],
          valueType: 'multi',
          dataType: 'text',
          listValues: [],
          sortOrder: 0,
          NeedApiRequest: false,
          default: []
        });
        combinedParams.add(combinedFilterParam);
      }
      
      // Keep product segment filter as separate parameter
      if (this.productSegment && this.productSegment.id) {
        const productSegmentParam = new ParamModel({
          name: 'Product Segment',
          code: 'product_segment_id',
          value: [this.productSegment.id.toString()],
          valueType: 'multi',
          dataType: 'number',
          listValues: [],
          sortOrder: 0,
          NeedApiRequest: false,
          default: []
        });
        combinedParams.add(productSegmentParam);
      }
      
      const result = await buyingCalendarModel.fetchBuyingCalendar(
        this.viewMode,
        this.selectedDateRange,
        this.locationIdsForApi,
        combinedParams,
        this.historicalDateRange,
        this.productLimitPerSupplier,
        this.selectedDimension,
      );
      this.suggestions = result.periods;
      this.currentDimension = result.dimension;
      
      // Clean up stale expanded group entries after data refresh
      this.cleanupExpandedGroups();
    } finally {
      this.loading = false;
    }
  }

  // Fetch suppliers for the dropdown
  async fetchSuppliers() {
    try {
      // Get the suppliers list and tax types
      await Promise.all([
        this.suppliers.fetch(),
        this.taxTypeList.fetch().catch(() => {}),
      ]);
      
      // Map suppliers to options for dropdown
      this.supplierOptions = [
        { text: 'All Suppliers', value: 'all' },
        ...this.suppliers.items.map((supplier: any) => ({
          text: supplier.name,
          value: supplier.id
        }))
      ];
    } catch (error) {
      this.handleError(error as Error, 'Error loading suppliers', false);
    }
  }

  // NEW: Handle historical date range changes from ReorderUserParams
  private onHistoricalDateRangeChanged(dateRange: string[]): void {
    // Always use actual dates for API communication (positions [0] and [1])
    // The preset string (position [2]) is for frontend date calculation only
    if (dateRange.length >= 2 && dateRange[0] && dateRange[1] && dateRange[0] !== '' && dateRange[1] !== '') {
      // Use actual historical dates for API
      this.historicalDateRange = [dateRange[0], dateRange[1]];
    } else {
      // No valid dates available
      this.historicalDateRange = [];
    }
    // Note: Don't fetch immediately - wait for user to click Apply button
  }

  private async handleDataSyncReady(): Promise<void> {
    if (this.paramsLoaded) {
      await this.fetchBuyingCalendar();
    }
    // If paramsLoaded is false, onReorderParamsLoaded will handle the fetch
  }

  private async onReorderParamsLoaded(loadedParams: any): Promise<void> {
    try {
      // Params have been loaded from database, now we can fetch the calendar data
      console.log('Reorder params loaded, fetching calendar data...');
      
      // Update our local state with the loaded params
      if (loadedParams.reportParams) {
        this.reportParams = loadedParams.reportParams;
      }
      
      // Update historical date range if available
      if (loadedParams.dateRange) {
        this.onHistoricalDateRangeChanged(loadedParams.dateRange);
      }
      
      // Now fetch the buying calendar with the loaded parameters
      this.paramsLoaded = true;
      await this.fetchBuyingCalendar();
    } catch (error) {
      this.handleError(error as Error, 'Failed to load reorder parameters and calendar data');
      // Ensure loading is set to false if there's an error
      this.loading = false;
    }
  }

  private onReorderParamsSaved(params: any): void {
    console.log('Reorder params saved to database');
    // Optionally show success notification
  }

  private onReorderParamsSaveError(error: any): void {
    console.error('Failed to save reorder params:', error);
    // Optionally show error notification
    // EventBus.$emit('show-error', 'Failed to save preferences. Changes are saved locally.');
  }

  // NEW: Filter data watcher
  @Watch("filterData", { deep: true })
  private async onFilterDataChange(): Promise<void> {
    this.filterColumns = this.filterData.filterColumns || new FilterColumnList();
    this.productSegment = this.filterData.productSegment || new ProductSegmentModel();
    if (this.settingsLoading) return;
    this.debouncedSaveSettings();
    await this.fetchBuyingCalendar();
  }

  // NEW: Remove individual filter
  public async removeFilter(filter: FilterColumnModel): Promise<void> {
    const index = this.filterColumns.items.findIndex(f => f.code === filter.code);
    if (index > -1) {
      this.filterColumns.items.splice(index, 1);
      await this.fetchBuyingCalendar();
    }
  }

  // NEW: Clear all filters
  public async clearFilters(): Promise<void> {
    this.filterColumns.items = [];
    this.productSegment = new ProductSegmentModel(); // Reset product segment
    this.initProductSegment = new ProductSegmentModel(); // Reset init product segment
    // Also update filterData to sync with ReportFilter component
    this.filterData = {
      ...this.filterData,
      filterColumns: new FilterColumnList(),
      productSegment: new ProductSegmentModel()
    };
    await this.fetchBuyingCalendar();
  }

  get getFormattedDateRange(): string {
    const [start, end] = this.selectedDates;
    return `${this.formatDate(new Date(start), 'short')} - ${this.formatDate(new Date(end), 'short')}`;
  }

  // Methods
  private formatDate(date: Date | string | null, format: 'short' | 'full' = 'short'): string {
    if (!date) return '-';
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return '-';
      
      const options: Intl.DateTimeFormatOptions = format === 'full' 
        ? { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
        : { year: 'numeric', month: 'short', day: 'numeric' };
      
      return dateObj.toLocaleDateString('en-US', options);
    } catch (error) {
      this.handleError(error as Error, 'Error formatting date', false);
      return '-';
    }
  }

  private formatDateKey(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private formatCurrency(value: any): string {
    return CurrencyHelper.formatCurrency(value);
  }

  private getSupplierCurrencyCode(supplier: any): string {
    if (!supplier || !supplier.id) return CurrencyHelper.getStoreCurrency();
    const record = this.suppliers.items.find((s: any) => s.id == supplier.id);
    return record?.currency_code || CurrencyHelper.getStoreCurrency();
  }

  private formatSupplierCurrency(value: any, supplier: any): string {
    return CurrencyHelper.formatCurrencyWithCode(
      typeof value === 'string' ? parseFloat(value) : (value || 0),
      this.getSupplierCurrencyCode(supplier)
    );
  }

  // CSV Export functionality
  private exportSupplierProductsToCSV(supplier: any): void {
    if (!supplier || !supplier.products || supplier.products.length === 0) {
      EventBus.$emit('show-snackbar', {
        message: 'No products available to export',
        color: 'warning'
      });
      return;
    }

    try {
      // CSV headers — exclude product-level columns in SKU mode
      const headers = [
        ...(this.isSkuMode ? [] : ['Product Name']),
        'SKU',
        'Barcode',
        'Product Type',
        ...(this.isSkuMode ? [] : ['Product Status']),
        'Supplier',
        'Current Stock',
        'Incoming Stock',
        'Reorder Quantity',
        'Reorder Point',
        'Reorder Date',
        'Lead Time (Days)',
        'Daily Sales Avg',
        'Total Sales',
        'Days Remaining',
        'Unit Cost',
        'Stock Value',
        'Total Reorder Value'
      ];

      // CSV data rows — match header columns
      const csvData = supplier.products.map((product: any) => [
        ...(this.isSkuMode ? [] : [this.sanitizeCSVValue(product['Item[Item Description]'] || '')]),
        this.sanitizeCSVValue(product.product_sku !== 'null' ? product.product_sku : ''),
        this.sanitizeCSVValue(product.product_barcode || ''),
        this.sanitizeCSVValue(product.product_type || ''),
        ...(this.isSkuMode ? [] : [this.sanitizeCSVValue(product.product_status || '')]),
        this.sanitizeCSVValue(product.supplier || supplier.name || ''),
        product['[Actual Inventory Quantity]'] || 0,
        product['[incoming_inventory_quantity]'] || 0,
        product['[inventory_replenishment]'] || 0,
        product['[reorder_point]'] || 0,
        this.formatDate(product['[reorder_date]']),
        product['[vendor_lead_time]'] || 0,
        this.formatNumber(product['[average_daily_units_sold_param]']) || 0,
        product['[sold_quantity_param]'] || 0,
        this.formatNumber(product['[stock_days_inventory_planning]']) || 0,
        this.formatSupplierCurrencyForCSV(product['[average_unit_cost]'], supplier) || 0,
        this.formatCurrencyForCSV(product['[Actual Inventory Cost]']) || 0,
        this.formatSupplierCurrencyForCSV(product['[replenishment_inventory_cost]'], supplier) || 0
      ]);

      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map((row: any[]) => row.map((field: any) => `"${field}"`).join(','))
        .join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${this.sanitizeFileName(supplier.name)}_reorder_products_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success message
      EventBus.$emit('show-snackbar', {
        message: `Exported ${supplier.products.length} products for ${supplier.name}`,
        color: 'success'
      });

    } catch (error) {
      this.handleError(error as Error, 'Failed to export CSV', true);
    }
  }

  // Helper function to sanitize CSV values
  private sanitizeCSVValue(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).replace(/"/g, '""'); // Escape quotes for CSV
  }

  // Helper function to format currency for CSV (remove currency symbols)
  private formatCurrencyForCSV(value: any): string {
    if (!value || isNaN(value)) return '0';
    return parseFloat(value).toFixed(2);
  }

  private formatSupplierCurrencyForCSV(value: any, supplier: any): string {
    const parsed = typeof value === 'string' ? parseFloat(value) : (value || 0);
    if (isNaN(parsed)) return '0';
    const decimals = CurrencyHelper.getDecimalPlaces(this.getSupplierCurrencyCode(supplier));
    return parsed.toFixed(decimals);
  }

  // Helper function to sanitize filename
  private sanitizeFileName(filename: string): string {
    return filename.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  }

  private calculateOrderValue(quantity: number, unitPrice: number): string {
    return CurrencyHelper.formatCurrency(quantity * unitPrice);
  }

  private calculateSupplierTotalOrderValue(products: IProduct[]): string {
    const total = products.reduce((sum, product) => 
      sum + (product.reorderQuantity * product.unitPrice), 0
    );
    return this.formatCurrency(total);
  }

  private getPeriodKey(date: Date): string {
    switch (this.viewMode) {
      case 'week':
        // Get the Monday of the week
        const monday = new Date(date);
        monday.setDate(monday.getDate() - monday.getDay() + 1);
        return this.formatDateKey(monday);
      case 'month':
        // Get the first day of the month
        return date.toISOString().slice(0, 7); // YYYY-MM
      default:
        return this.formatDateKey(date);
    }
  }

  private getPeriodRange(date: Date): { start: Date; end: Date } {
    const start = new Date(date);
    const end = new Date(date);

    switch (this.viewMode) {
      case 'week':
        // Set to Monday
        start.setDate(start.getDate() - start.getDay() + 1);
        // Set to Sunday
        end.setDate(end.getDate() - end.getDay() + 7);
        break;
      case 'month':
        // Set to first day of month
        start.setDate(1);
        // Set to last day of month
        end.setMonth(end.getMonth() + 1);
        end.setDate(0);
        break;
      default:
        // For daily view, start and end are the same
        break;
    }

    return { start, end };
  }

  private getFormattedPeriodLabel(date: Date): string {
    switch (this.viewMode) {
      case 'week':
        const { start, end } = this.getPeriodRange(date);
        return `Week of ${this.formatDate(start, 'short')} - ${this.formatDate(end, 'short')}`;
      case 'month':
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      default:
        return this.formatDate(date);
    }
  }

  private getExpansionKey(periodIndex: number, supplierId: number | string): string {
    return `expansion-${periodIndex}-${supplierId}`;
  }

  private toggleGroup(periodIndex: number, supplierId: number | string): void {
    const key = this.getExpansionKey(periodIndex, supplierId);
    this.$set(this.expandedGroups, key, !this.expandedGroups[key]);
  }

  // on viewMode change
  @Watch('viewMode')
  async onViewModeChange(): Promise<void> {
    this._selectedDates = this.getDateRange();
    if (this.settingsLoading) return;
    this.debouncedSaveSettings();
    await this.fetchBuyingCalendar();
  }

  @Watch('displayMode')
  private onDisplayModeChanged(): void {
    if (this.settingsLoading) return;
    this.debouncedSaveSettings();
  }

  // Watch supplier filter changes
  @Watch('supplierFilter')
  private async onSupplierFilterChanged(): Promise<void> {
    if (this.settingsLoading) return;
    this.debouncedSaveSettings();
    await this.fetchBuyingCalendar();
  }

  @Watch('selectedDimension')
  private async onDimensionChanged(): Promise<void> {
    if (this.settingsLoading) return;
    this.debouncedSaveSettings();
    await this.fetchBuyingCalendar();
  }

  private isReorderDateNear(dateStr: string): boolean {
    const reorderDate = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((reorderDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    return diffDays <= 7;
  }

  private async fetchLocations(): Promise<void> {
    this.loadingLocations = true;
    try {
      await this.storeLocationList.fetch();
      this.locations = this.storeLocationList.items;
    } catch (error) {
      this.handleError(error as Error, 'Failed to load store locations');
    } finally {
      this.loadingLocations = false;
    }
  }

  // NEW: Validate that inventory_replenishment measure is available
  private loadMeasures(): void {
    // Measures are already loaded as a prop, just validate inventory_replenishment exists
    const inventoryMeasure = this.measures.items.find(m => m.code === 'inventory_replenishment');
    if (!inventoryMeasure) {
      console.warn('inventory_replenishment measure not found in measures list');
    }
  }

  private async handleLocationFilterUpdate(filterList: FilterColumnList): Promise<void> {
    this.activeLocationFilters = filterList;
    if (this.settingsLoading) return;
    this.debouncedSaveSettings();
    await this.fetchBuyingCalendar();
  }

  get weekDays(): string[] {
    switch (this.viewMode) {
      case 'day':
        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      case 'week':
        return Array.from({ length: 8 }, (_, i) => {
          const startDate = new Date(this.selectedDates[0]);
          const weekStart = new Date(startDate);
          weekStart.setDate(startDate.getDate() + (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          return `Week ${i + 1} (${this.formatDate(weekStart, 'short')} - ${this.formatDate(weekEnd, 'short')})`;
        });
      case 'month':
        return Array.from({ length: 6 }, (_, i) => {
          const date = new Date(this.selectedDates[0]);
          date.setMonth(date.getMonth() + i);
          return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        });
    }
  }

  get calendarTitle(): string {
    const today = new Date();
    
    switch (this.viewMode) {
      case 'day':
        // Follow calendarDays logic - show 14 days starting from today
        const dayStart = today;
        const dayEnd = new Date(today);
        dayEnd.setDate(today.getDate() + 13);
        return `${this.formatDate(dayStart, 'short')} - ${this.formatDate(dayEnd, 'short')} (14 Days)`;
        
      case 'week':
        // Follow calendarDays logic - show 8 weeks starting from Monday of current week
        const weekStart = new Date(today);
        const dayOfWeek = weekStart.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        weekStart.setDate(weekStart.getDate() + daysToMonday);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + (8 * 7) - 1); // End of 8th week
        return `${this.formatDate(weekStart, 'short')} - ${this.formatDate(weekEnd, 'short')} (8 Weeks)`;
        
      case 'month':
        // Follow calendarDays logic - show 6 months starting from current month
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 6, 0); // Last day of 6th month
        return `${monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${monthEnd.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} (6 Months)`;
        
      default:
        const startDate = new Date(this.selectedDates[0]);
        const endDate = new Date(this.selectedDates[this.selectedDates.length - 1]);
        return `${this.formatDate(startDate, 'short')} - ${this.formatDate(endDate, 'short')}`;
    }
  }

  get calendarDays(): Array<Array<{ 
    date: string; 
    dayNumber: number; 
    currentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    dateRange?: { start: Date; end: Date };
  }>> {
    const weeks: Array<Array<any>> = [];
    const today = new Date();
    const startDate = new Date(this.selectedDates[0]);
    
    switch (this.viewMode) {
      case 'day':
        // Show next 14 days in two rows
        let dayWeek = [{
          date: this.formatDateKey(today),
          dayNumber: today.getDate(),
          currentMonth: true,
          isToday: true,
          isSelected: true
        }];
        
        for (let i = 1; i < 14; i++) {
          const nextDay = new Date(today);
          nextDay.setDate(today.getDate() + i);
          dayWeek.push({
            date: this.formatDateKey(nextDay),
            dayNumber: nextDay.getDate(),
            currentMonth: nextDay.getMonth() === today.getMonth(),
            isToday: false,
            isSelected: false
          });
          
          // Split into rows of 7 days each
          if (dayWeek.length === 7) {
            weeks.push(dayWeek);
            dayWeek = [];
          }
        }
        
        // Add any remaining days
        if (dayWeek.length > 0) {
          weeks.push(dayWeek);
        }
        break;

      case 'week':
        // Show next 8 weeks in a grid (2x4)
        let weekRow: any[] = [];
        for (let i = 0; i < 8; i++) {
          const weekStart = new Date(today);
          const dayOfWeek = weekStart.getDay();
          const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          weekStart.setDate(weekStart.getDate() + daysToMonday + (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);

          weekRow.push({
            date: this.formatDateKey(weekStart),
            dayNumber: this.getWeekNumber(weekStart),
            currentMonth: true,
            isToday: i === 0,
            isSelected: i === 0,
            dateRange: { start: weekStart, end: weekEnd }
          });

          if (weekRow.length === 4) {
            weeks.push(weekRow);
            weekRow = [];
          }
        }
        if (weekRow.length > 0) {
          weeks.push(weekRow);
        }
        break;

      case 'month':
        // Show next 6 months in a grid (2x3)
        let monthRow: any[] = [];
        for (let i = 0; i < 6; i++) {
          const monthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
          const monthEnd = new Date(today.getFullYear(), today.getMonth() + i + 1, 0);
          
          // Format as YYYY-MM to match the suggestion period format
          const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;

          monthRow.push({
            date: monthKey,
            dayNumber: monthDate.getMonth() + 1,
            currentMonth: true,
            isToday: i === 0,
            isSelected: i === 0,
            dateRange: { 
              start: monthDate, 
              end: monthEnd 
            }
          });

          if (monthRow.length === 3) {
            weeks.push(monthRow);
            monthRow = [];
          }
        }
        break;
    }
    
    return weeks;
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return this.formatDateKey(date) === this.formatDateKey(today);
  }

  private getDateRange(): string[] {
    const today = new Date();
    let start: Date;
    let end: Date;

    switch (this.viewMode) {
      case 'day':
        // Start from today, show next 14 days
        start = today;
        end = new Date(today);
        end.setDate(today.getDate() + 13);
        break;

      case 'week':
        // Start from Monday of current week, show next 8 weeks
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay() + 1); // Monday
        end = new Date(start);
        end.setDate(start.getDate() + (8 * 7) - 1); // End of 8th week
        break;

      case 'month':
        // Start from first day of current month, show next 6 months
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 6, 0);
        break;
    }

    return [
      this.formatDateKey(start),
      this.formatDateKey(end)
    ];
  }

  get selectedDates(): string[] {
    return this._selectedDates;
  }

  set selectedDates(value: string[]) {
    this._selectedDates = value;
  }

  private hasSuggestionsForDate(date: string): boolean {
    if (this.viewMode === 'month') {
      return this.suggestions.some((s: any) => s.period === date);
    }
    
    if (this.viewMode === 'week') {
      // For week view, check if any suggestion falls within the week's date range
      return this.suggestions.some((s: any) => {
        const suggestionDate = new Date(s.period);
        const weekStart = new Date(date);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        return suggestionDate >= weekStart && suggestionDate <= weekEnd;
      });
    }
    
    // For day view
    return this.suggestions.some((s: any) => 
      this.formatDateKey(new Date(s.period)) === date
    );
  }

  private getSuggestionsForDate(date: string): any[] {
    if (this.viewMode === 'month') {
      return this.suggestions.filter((s: any) => s.period === date);
    }
    
    if (this.viewMode === 'week') {
      // For week view, get suggestions that fall within the week's date range
      return this.suggestions.filter((s: any) => {
        const suggestionDate = new Date(s.period);
        const weekStart = new Date(date);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        return suggestionDate >= weekStart && suggestionDate <= weekEnd;
      });
    }
    
    // For day view
    return this.suggestions.filter((s: any) => 
      this.formatDateKey(new Date(s.period)) === date
    );
  }

  private showSupplierDetails(supplier: any, date: string): void {
    this.selectedSupplierDetails = supplier;
    this.showSupplierDialog = true;
  }

  // Add this helper method to get week number
  private getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }


  // Add these methods to the component
  private getStockLevelColor(product: any): string {
    const stockLevel = product['[Actual Inventory Quantity]'] || 0;
    const reorderQuantity = product['[inventory_replenishment]'] || 0;
    
    // If we have less than half of the recommended reorder quantity, it's critical
    if (stockLevel <= (reorderQuantity * 0.5)) return 'error';
    
    // If we have less than the recommended reorder quantity, it's a warning
    if (stockLevel <= reorderQuantity) return 'warning';
    
    // Otherwise, we're good
    return 'success';
  }

  private formatNumber(value: any, decimals: number = 2): string {
    if (value === null || value === undefined) return '0.00';
    if (typeof value === 'string') value = parseFloat(value);
    if (typeof value !== 'number' || isNaN(value)) return '0.00';
    return value.toFixed(decimals);
  }


  // Dialog for selecting delivery location
  private openDeliveryLocationDialog(supplier: any): void {
    this.currentSupplierForPoCreation = supplier;
    this.selectedDeliveryLocationIdForPO = null; // Reset previous selection
    this.deliveryLocationDialogVisible = true;
    //this.showSupplierDialog = false;
  }

  private cancelDeliveryLocationSelection(): void {
    this.deliveryLocationDialogVisible = false;
    this.currentSupplierForPoCreation = null;
  }

  private async confirmAndCreatePurchaseOrder(): Promise<void> {
    if (!this.currentSupplierForPoCreation || !this.selectedDeliveryLocationIdForPO) {
      EventBus.$emit('show-snackbar', {
        message: 'Supplier or delivery location missing.',
        color: 'error'
      });
      return;
    }

    const supplier = this.currentSupplierForPoCreation;
    const deliveryLocationId = this.selectedDeliveryLocationIdForPO;

    // Ensure supplierPoCreationState has an entry for the supplier
    if (!this.supplierPoCreationState[supplier.id]) {
      this.$set(this.supplierPoCreationState, supplier.id, { loading: false, poCreated: false });
    }
    this.$set(this.supplierPoCreationState[supplier.id], 'loading', true);
    this.deliveryLocationDialogVisible = false; // Close dialog before processing

    try {
      // Resolve supplier's default tax type
      let supplierDefaultTaxType: TaxTypeModel | null = null;
      const supplierRecord = this.suppliers.items.find((s: any) => s.id == supplier.id);
      const defaultTaxTypeId = supplierRecord?.options?.financial?.default_tax_type_id;
      if (defaultTaxTypeId) {
        supplierDefaultTaxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id == defaultTaxTypeId) || null;
      }
      if (!supplierDefaultTaxType) {
        supplierDefaultTaxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.isDefault) || null;
      }

      // Map products to IPurchaseOrderItem[]
      const supplierCurrencyCode = this.getSupplierCurrencyCode(supplier);
      const poItems: IPurchaseOrderItem[] = supplier.products.map((product: any) => {
        const qty = Number(product['[inventory_replenishment]'] || 0);
        const price = Number(product['[average_unit_cost]'] || 0);
        const taxExtra = supplierDefaultTaxType ? {
          financial: {
            tax_type_id: supplierDefaultTaxType.id,
            tax_type_name: supplierDefaultTaxType.name,
            tax_rate: supplierDefaultTaxType.rate,
            tax_amount: CurrencyHelper.roundForCurrency(qty * price * supplierDefaultTaxType.rate, supplierCurrencyCode),
          }
        } : undefined;

        return {
          variantId: product.variant_id,
          variantImage: product.variant_image,
          productName: product['Item[Item Description]'] || 'Unknown Product',
          variant: product.variant || '',
          sku: product.product_sku || '',
          supplierSku: product.supplier_sku || product.supplierSku || undefined,
          orderedQuantity: qty,
          receivedQuantity: 0,
          unitPrice: price,
          totalPrice: qty * price,
          extra: taxExtra,
        };
      }).filter((item: IPurchaseOrderItem) => item.orderedQuantity > 0);

      if (poItems.length === 0) {
        EventBus.$emit("show-snackbar", {
          message: `No items with valid quantity to order for ${supplier.name}.`,
          color: "warning"
        });
        this.$set(this.supplierPoCreationState[supplier.id], 'loading', false);
        return;
      }

      // Calculate Expected Delivery Date
      let expectedDeliveryDateISOString: string | undefined = undefined;
      const leadTime = supplier.leadTime;
      if (typeof leadTime === 'number' && leadTime >= 0) {
          const orderDate = new Date();
          const expectedDate = new Date(orderDate.setDate(orderDate.getDate() + leadTime));
          expectedDeliveryDateISOString = expectedDate.toISOString();
      }

      const newPo = new PurchaseOrderModel({
        poNumber: this.generatePoNumber(),
        supplierId: this.isUnassignedSupplier(supplier) ? undefined : Number(supplier.id),
        supplierName: this.isUnassignedSupplier(supplier) ? undefined : supplier.name,
        status: 'draft',
        orderDate: new Date().toISOString(),
        expectedDeliveryDate: expectedDeliveryDateISOString,
        locationId: Number(deliveryLocationId),
        currencyCode: supplierCurrencyCode,
        items: poItems,
      });

      await newPo.save();

      this.$set(this.supplierPoCreationState[supplier.id], 'poId', newPo.id);
      this.$set(this.supplierPoCreationState[supplier.id], 'poCreated', true);

      EventBus.$emit("show-snackbar", {
        message: `Purchase Order ${newPo.poNumber || '#' + newPo.id} for ${supplier.name} created.`,
        color: "success"
      });
      this.$router.push(`/purchase-orders/${newPo.id}`);

      // Optionally: Remove supplier from suggestions or update UI if needed
      // This part depends on desired behavior after PO creation

    } catch (error: any) {
      this.handleError(error as Error, `Failed to create purchase order for supplier ${supplier.name}`);
    } finally {
      this.$set(this.supplierPoCreationState[supplier.id], 'loading', false);
      this.currentSupplierForPoCreation = null; // Reset after operation
    }
  }

  private generatePoNumber(): string {
    const prefix = 'PO';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randomNumber = Math.floor(Math.random() * 90000 + 10000); // 5-digit random number
    return `${prefix}${date}-${randomNumber}`;
  }

  // Method to initiate PO creation (opens delivery location dialog)
  private initiatePurchaseOrderCreation(supplier: any): void {
    // Check if PO already created for this supplier in this session to avoid re-opening dialog
    if (this.supplierPoCreationState[supplier.id] && this.supplierPoCreationState[supplier.id].poCreated) {
        EventBus.$emit('show-snackbar', {
            message: `Purchase Order for ${this.isUnassignedSupplier(supplier) ? 'unassigned products' : supplier.name} already created in this session.`,
            color: 'info'
        });
        if(this.supplierPoCreationState[supplier.id].poId) {
            this.$router.push(`/purchase-orders/${this.supplierPoCreationState[supplier.id].poId}`);
        }
        return;
    }
    this.openDeliveryLocationDialog(supplier);
  }

  public deliveryLocationRule(v: string | null): boolean | string {
    return !!v || 'Delivery location is required';
  }

  private isUnassignedSupplier(supplier: any): boolean {
    return supplier.id === 0 || !supplier.id || supplier.id === 'unassigned';
  }

  private goToSuppliers(): void {
    this.$router.push('/suppliers');
  }

  private openInteractiveGuide(): void {
    window.open('https://app.arcade.software/share/JRBnwr5vakSiIlJUtAKG', '_blank');
  }
}
